import { useCallback, useSyncExternalStore } from 'react';

import type { QueryClient } from '@tanstack/react-query';
import type { AnyRoute, AnyRouter, ParsedLocation } from '@tanstack/react-router';

import type { EntityNames } from './entityNames';
import type { Probe } from './probe';
import type { SingleRootDocument } from './types';

// Path + query identity; the hash never changes which document the server rendered.
export const documentKey = (href: string): string => {
  const url = new URL(href, window.location.origin);
  return `${url.pathname}${url.search}`;
};

export const isServedDocument = (location: ParsedLocation, documentHref: string): boolean =>
  documentKey(location.href) === documentKey(documentHref);

/**
 * The server's document for the locations the shell shows: the served one from the first load,
 * and the latest probe answer. There is one probed slot, so going back to an earlier gap probes
 * again and the server re-decides it (permissions, the record, flash), as a reload would.
 */
export interface ShellDocuments {
  /** The document for a location's path and query, preferring the probe answer. */
  forHref: (href: string) => SingleRootDocument | null;
  /**
   * Stores a probe answer for `href`. `fresh` marks one the shell answered before navigating
   * there (a followed redirect), so arriving at it does not probe again.
   */
  accept: (href: string, document: SingleRootDocument, fresh?: boolean) => void;
  /** True once for a fresh answer at `href`. */
  takeFresh: (href: string) => boolean;
  /** Changes with every accepted answer. Gap hits are counted once per scope. */
  scope: () => number;
  subscribe: (listener: () => void) => () => void;
}

export const createShellDocuments = (served: SingleRootDocument): ShellDocuments => {
  let probed: { key: string; document: SingleRootDocument; fresh: boolean } | null = null;
  let generation = 0;
  const listeners = new Set<() => void>();

  return {
    forHref: (href) => {
      const key = documentKey(href);
      if (probed?.key === key) return probed.document;
      return documentKey(served.href) === key ? served : null;
    },
    accept: (href, document, fresh = false) => {
      probed = { key: documentKey(href), document, fresh };
      generation += 1;
      listeners.forEach((listener) => listener());
    },
    takeFresh: (href) => {
      if (!probed?.fresh || probed.key !== documentKey(href)) return false;
      probed.fresh = false;
      return true;
    },
    scope: () => generation,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
};

// What the shell router carries in its context (`createShellRouter`).
export interface ShellRouterContext {
  queryClient: QueryClient;
  admittedRouteFor: (pathname: string) => AnyRoute | undefined;
  documents: ShellDocuments;
  probe: Probe;
  /** Absolute URL of the location the latest navigation left. */
  referrer: () => string;
  /** Names for the entity crumbs routes declare without one. */
  entityNames: EntityNames;
}

export const shellContextOf = (router: AnyRouter): ShellRouterContext | null => {
  const context = router.options.context as Partial<ShellRouterContext> | undefined;
  return context?.documents ? (context as ShellRouterContext) : null;
};

const shellDocumentsOf = (router: AnyRouter): ShellDocuments | null =>
  shellContextOf(router)?.documents ?? null;

// The server's document for the router's current location, if it has one.
export const locationDocument = (router: AnyRouter): SingleRootDocument | null =>
  shellDocumentsOf(router)?.forHref(router.state.location.href) ?? null;

const noDocuments = () => () => undefined;

// The server's document for `href`, re-rendering when a probe answers.
export const useLocationDocument = (router: AnyRouter, href: string): SingleRootDocument | null => {
  const documents = shellDocumentsOf(router);
  const getSnapshot = useCallback(() => documents?.forHref(href) ?? null, [documents, href]);
  return useSyncExternalStore(documents?.subscribe ?? noDocuments, getSnapshot);
};
